import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, parseISO } from 'date-fns';
import { Button } from '../../components/Button';
import { Field, Input, Select, Textarea } from '../../components/Field';
import { useDb } from '../../db/hooks';
import { useSettings } from '../../data/settings';
import { useSessions } from '../../data/sessions';
import { deleteExpense, EXPENSE_CATEGORY_LABELS, saveExpense, type Expense, type ExpenseCategory } from '../../data/expenses';
import { fromMinorUnits, toMinorUnits } from '../../lib/currency';
import { inputValue, optionalText } from '../../lib/forms';
import { localToday } from '../sessions/fields';

const schema = (currency: string) =>
  z
    .object({
      expense_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose a date.').refine((value) => value <= localToday(), 'Choose a date that isn’t in the future.'),
      amount: z.string().refine((value) => toMinorUnits(value, currency) != null, 'Enter an amount, like 25.50.'),
      category: z.enum(['session_fee', 'medication', 'lab', 'transport', 'consultation', 'supplies', 'other']),
      description: z.string().trim().min(1, 'Say what it was for.').max(120),
      session_id: z.string().transform((value) => (value === '' ? null : value)),
      notes: optionalText(500)
    })
    .transform(({ amount, ...values }) => ({ ...values, amount_minor: toMinorUnits(amount, currency)! }));

export function ExpenseForm({ entry, onDone }: { entry?: Expense; onDone: () => void }) {
  const db = useDb();
  const { settings } = useSettings();
  const { sessions } = useSessions();
  const [confirm, setConfirm] = useState(false);
  const currency = settings.currency;
  const resolver = zodResolver(schema(currency));
  const form = useForm<z.input<ReturnType<typeof schema>>, unknown, z.output<ReturnType<typeof schema>>>({
    resolver,
    defaultValues: {
      expense_date: entry?.expense_date ?? localToday(),
      amount: entry ? fromMinorUnits(entry.amount_minor, currency) : '',
      category: entry?.category ?? 'session_fee',
      description: entry?.description ?? '',
      session_id: entry?.session_id ?? '',
      notes: inputValue(entry?.notes)
    }
  });
  const { errors, isSubmitting } = form.formState;
  const date = form.watch('expense_date');
  const sameDay = sessions.filter((session) => session.session_date === date && session.status !== 'missed');

  return (
    <form
      noValidate
      className="space-y-4"
      onSubmit={form.handleSubmit(async (values) => {
        try {
          await saveExpense(db, entry?.id, values);
          onDone();
        } catch (error) {
          form.setError('root', { message: error instanceof Error ? error.message : String(error) });
        }
      })}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label={`Amount (${currency})`} error={errors.amount?.message}>
          <Input inputMode="decimal" autoFocus={!entry} {...form.register('amount')} />
        </Field>
        <Field label="Date" error={errors.expense_date?.message}>
          <Input type="date" {...form.register('expense_date')} />
        </Field>
      </div>
      <Field label="Category">
        <Select {...form.register('category')}>
          {(Object.keys(EXPENSE_CATEGORY_LABELS) as ExpenseCategory[]).map((category) => (
            <option key={category} value={category}>
              {EXPENSE_CATEGORY_LABELS[category]}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="What for" error={errors.description?.message}>
        <Input {...form.register('description')} placeholder="e.g. Session fee, taxi to the centre" />
      </Field>
      {sameDay.length ? (
        <Field label="Session (optional)">
          <Select {...form.register('session_id')}>
            <option value="">Not linked</option>
            {sameDay.map((session) => (
              <option key={session.id} value={session.id}>
                Session on {format(parseISO(session.session_date), 'EEE d MMM')}
                {session.start_time ? ` at ${session.start_time}` : ''}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      <Field label="Notes (optional)" error={errors.notes?.message}>
        <Textarea {...form.register('notes')} />
      </Field>
      {errors.root?.message ? <p className="text-sm text-clinical-danger">{errors.root.message}</p> : null}
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        Save
      </Button>
      {entry ? (
        confirm ? (
          <Button type="button" variant="danger" className="w-full" onClick={() => void deleteExpense(db, entry.id).then(onDone)}>
            Delete this expense
          </Button>
        ) : (
          <Button type="button" variant="ghost" className="w-full !text-clinical-danger" onClick={() => setConfirm(true)}>
            Delete
          </Button>
        )
      ) : null}
    </form>
  );
}
